// Today is a dashboard: the coach's sentence, the next ride as the hero (drag it, re-route it,
// book it), then a grid of tiles that each answer one thing at a glance and open their own page:
// the week, Paris, the weather at the start, the light, fitness, the last ride, the weekend's
// route. Legs on a ride morning and the ready list the evening before come by the moment.
// One action at the foot, chosen by the moment.
import { useEffect, useState } from "preact/hooks";
import { useMusette, bookRides, setFeel, toggleReady, pullRefresh, rideDetail, setLegs, setRoute, addRide, getState, routeOuts, adoptExplore, routeDetail, intervalsFit } from "../lib/store";
import { routeIdeas, type Wind } from "../lib/ideas";
import { newGround, litOnOsm, type Explore } from "../lib/explore";
import { longest } from "../lib/analysis";
import { londonNow } from "../lib/day";
import { Pull } from "../components/Pull";
import { coachLine } from "../lib/coach";
import { dateLabel, dayLong, dayShort, dayNum, mondayOf, addDays, todayIso, daysBetween, dow, weeksTo } from "../lib/dates";
import { sunTimes, hhmm, toMin } from "../lib/sun";
import { forecastAt, forecastSpan, windOver, type Forecast, type Span } from "../lib/weather";
import { darkMinutes, type Kept } from "../lib/band";
import { momentFor, readyItems, FEELS } from "../lib/day";
import { purposeFor, fuelFor } from "../lib/purpose";
import { real } from "../lib/analysis";
import { Head } from "../components/Head";
import { Sheet, Ring } from "../components/Sheet";
import { RouteMap } from "../components/RouteMap";
import { fitness } from "../lib/fitness";
import { useMemo } from "preact/hooks";
import { RideEditor } from "../components/RideEditor";
import { useToast } from "../components/Sheet";
import { photo } from "../data/photos";
import type { PlanDay, Legs } from "../lib/types";
import { homeOf } from "../data/empty";

export function Today() {
  const s = useMusette();
  const today = todayIso();
  const monday = mondayOf(today);
  const { show, node: toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 60_000); return () => clearInterval(t); }, []);

  const days = s.plan.flatMap((w) => w.days);
  const weekDays = s.plan.find((p) => p.monday === monday)?.days ?? [];
  const live = days.filter((d) => d.type === "ride" && d.status !== "cancelled" && d.start && d.end);
  const next = live.find((d) => d.date >= today && d.status !== "done" && !s.rides.some((r) => r.d === d.date && r.km >= 15));
  const moment = momentFor(s);
  // Today's ride is over with nothing on Strava: the card says so and carries the move (checklist 14).
  const missedNow = moment.kind === "missed";
  const weekNo = rampWeekNo(s.ramp, monday);
  const route = next?.route ? s.routes.find((r) => r.id === next.route) : undefined;

  const [fc, setFc] = useState<Forecast | null>(null);
  // The forecast moves through the day: read it again every half hour and whenever the app comes back.
  const [wx, setWx] = useState(0);
  useEffect(() => { const t = setInterval(() => setWx((n) => n + 1), 30 * 60_000); const v = () => { if (document.visibilityState === "visible") setWx((n) => n + 1); }; document.addEventListener("visibilitychange", v); return () => { clearInterval(t); document.removeEventListener("visibilitychange", v); }; }, []);
  useEffect(() => { if (next?.start) forecastAt(homeOf(s).lat, homeOf(s).lon, next.date, next.start).then(setFc); }, [next?.date, next?.start, wx]);
  // The ride's whole span, for rain timing and the strongest wind, shown on the day and the evening before.
  const [span, setSpan] = useState<Span | null>(null);
  useEffect(() => { if (next?.start && next.end) forecastSpan(homeOf(s).lat, homeOf(s).lon, next.date, next.start, next.end).then(setSpan).catch(() => setSpan(null)); }, [next?.date, next?.start, next?.end, wx]);
  const sun = next ? sunTimes(homeOf(s).lat, homeOf(s).lon, next.date) : null;
  const dark = next && sun ? Math.round(darkMinutes({ start: toMin(next.start!), end: toMin(next.end!) }, sun.rise, sun.set)) : 0;
  const fuel = next ? fuelFor(next) : null;
  const items = next ? readyItems(next.kit ?? fc?.kit ?? null, dark, fuel!.items) : [];
  const laid = new Set(next ? s.log?.ready?.[next.date] ?? [] : []);
  const lastRide = s.rides.filter(real).slice().sort((a, b) => (a.d < b.d ? 1 : -1))[0];
  const when = (d: PlanDay) => (d.date === today ? "Today" : dayLong(d.date));
  const [lastHead, setLastHead] = useState<string | null>(null);
  useEffect(() => { if (lastRide?.id) rideDetail(lastRide.id).then((x) => setLastHead(x?.writeup?.headline ?? null)); }, [lastRide?.id]);
  // Share my ride: for a ride that runs into the dark, one tap tells someone where you are and when you're back.
  const share = async () => {
    if (!next) return;
    const text = `Out on ${route?.n ?? "the city loop"}, home by ${next.end}.`;
    try { if (navigator.share) { await navigator.share({ text }); return; } await navigator.clipboard.writeText(text); show("Copied. Paste it to whoever needs it."); } catch { /* closed the share sheet */ }
  };
  // Legs this morning: one tap before midday, gone once answered.
  // Asked on a ride morning only.
  const askLegs = !!next && next.date === today && !s.log?.legs?.[today] && londonNow().min < 12 * 60;
  // The ready list: the evening before (from 17:00) and on the day.
  const now = londonNow().min;
  const showReady = !!next && (next.date === today || (next.date === addDays(today, 1) && now >= 17 * 60));
  const purpose = next ? purposeFor(s, next, Number(rampWeekNo(s.ramp, mondayOf(next.date))) || 1, next.date === today ? s.log?.legs?.[today] : null) : null;
  const [routesOpen, setRoutesOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [readyOpen, setReadyOpen] = useState(false);
  // The ride's own map, when its route has a file: the brief leads with the ride itself.
  const [rideTrack, setRideTrack] = useState<[number, number][] | null>(null);
  useEffect(() => { setRideTrack(null); const id = next?.route; if (!id || id === "city-loop") return; routeDetail(id).then((x) => setRideTrack(x?.track ?? null)).catch(() => {}); }, [next?.route]);
  const [rideWind, setRideWind] = useState<{ from: number; kmh: number } | null>(null);
  useEffect(() => { if (next?.start && next.end) windOver(homeOf(s).lat, homeOf(s).lon, next.date, next.start, next.end).then(setRideWind).catch(() => setRideWind(null)); }, [next?.date, next?.start, next?.end]);
  const compass = (deg: number) => ["N", "NE", "E", "SE", "S", "SW", "W", "NW"][Math.round(((deg % 360) + 360) % 360 / 45) % 8];
  const outIn = (() => { if (!next || next.date !== today) return null; const m = toMin(next.start!) - now; if (m <= 0) return toMin(next.end!) > now ? "Out now" : null; return m >= 60 ? `Out in ${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")}` : `Out in ${m} min`; })();
  const nextRoute = next ? s.routes.find((r) => r.id === (next.route ?? "city-loop")) : undefined;
  const proposed = !!next && next.status !== "booked" && next.status !== "done";
  const legs = (v: Legs) => { setLegs(today, v); show(v === "heavy" ? "Logged: heavy. The coach will ease the next ride." : `Logged: ${v}.`, () => setLegs(today, null)); };

  // Route ideas for the next long ride, with the wind where the route has a map.
  const [outs, setOuts] = useState<Record<string, number | null>>({});
  useEffect(() => { routeOuts().then(setOuts); }, [s.routes.length]);
  const base = routeIdeas(s, today, outs, null);
  const [wind, setWind] = useState<Wind | null>(null);
  useEffect(() => { if (base) windOver(homeOf(s).lat, homeOf(s).lon, base.date, base.start, base.day?.end ?? "12:00").then(setWind); }, [base?.date, base?.start]);
  const ideas = wind ? routeIdeas(s, today, outs, wind) : base;
  const pickIdea = async (id: string) => {
    if (!ideas) return;
    const i = ideas.ideas.find((x) => x.id === id)!;
    const was = ideas.day?.route ? { route: ideas.day.route, start: ideas.day.start!, end: ideas.day.end! } : null;
    if (!ideas.day || ideas.day.type !== "ride") await addRide(ideas.date);
    const m = await setRoute(ideas.date, id, hhmm(i.start), hhmm(i.end));
    const booked = getState().plan.flatMap((w) => w.days).find((d) => d.date === ideas.date)?.status === "booked";
    show(booked && typeof m === "string" ? m : `${dayLong(ideas.date)}: ${i.name}, ${hhmm(i.start)} to ${hhmm(i.end)}. Proposed; book it from the week.`, was ? () => setRoute(ideas.date, was.route, was.start, was.end) : undefined);
  };
  // New ground from Komoot for the same day: the best one that fits, loaded on demand.
  const [explore, setExplore] = useState<Explore[] | null>(null);
  useEffect(() => { import("../data/explore").then((m) => setExplore(m.EXPLORE)).catch(() => {}); }, []);
  const fresh = ideas && explore ? newGround(s, ideas.date, ideas.start, ideas.target, explore, wind)[0] : undefined;
  const rideNew = async () => {
    if (!fresh || !ideas) return;
    await adoptExplore(fresh.e);
    if (!ideas.day || ideas.day.type !== "ride") await addRide(ideas.date);
    await setRoute(ideas.date, fresh.e.id, hhmm(fresh.start), hhmm(fresh.end));
    show(`${dayLong(ideas.date)}: ${fresh.e.name}, ${hhmm(fresh.start)} to ${hhmm(fresh.end)}. In the library and proposed; book it from the week.`);
  };
  // Lit loops for the next weekday evening ride, from OpenStreetMap's lighting.
  const eve = live.find((d) => d.date >= today && d.status !== "done" && dow(d.date) >= 1 && dow(d.date) <= 5 && toMin(d.start!) >= 17 * 60 && !s.rides.some((r) => r.d === d.date && r.km >= 15));
  const litPicks = eve && explore ? newGround(s, eve.date, eve.start!, null, explore.filter(litOnOsm)).sort((a, b) => (b.e.light!.lit + b.e.light!.likely) - (a.e.light!.lit + a.e.light!.likely)).slice(0, 2) : [];
  const rideLit = async (g: (typeof litPicks)[number]) => {
    if (!eve) return;
    await adoptExplore(g.e);
    const m = await setRoute(eve.date, g.e.id, hhmm(g.start), hhmm(g.end));
    show(eve.status === "booked" && typeof m === "string" ? m : `${dayLong(eve.date)}: ${g.e.name}, ${hhmm(g.start)} to ${hhmm(g.end)}.`, () => setRoute(eve.date, eve.route ?? "city-loop", eve.start!, eve.end!));
  };
  const top = longest(s.rides);
  // Tiles
  const weekPlanned = weekDays.filter((d) => d.type === "ride" && d.status !== "cancelled").length;
  const weekDone = Array.from({ length: 7 }, (_, i) => addDays(monday, i)).filter((d) => s.rides.some((r) => r.d === d && r.km >= 15)).length;
  const weekKm = s.rides.filter((r) => r.d >= monday && r.d <= addDays(monday, 6)).reduce((a, r) => a + r.km, 0);
  const weekRamp = s.ramp.find((r) => r.w === monday);
  const weekTarget = weekRamp?.km ?? 0;
  // The same count as Week's ring: the ramp's ask, in rides or in km (checklist 1).
  const byRides = !(weekRamp?.km && !weekRamp.rides);
  const tileDone = byRides ? weekDone : Math.round(weekKm);
  const tileOf = byRides ? (weekRamp?.rides ?? Math.max(1, weekPlanned)) : weekTarget;
  const day1Km = s.stages[0]?.km.v ?? 207;
  const parisPct = Math.min(100, Math.round(((top?.km ?? 0) / day1Km) * 100));
  const parisWeeks = s.stages[0] ? weeksTo(today, s.stages[0].date) : 0;
  const [icu, setIcu] = useState<import("../lib/fitness").Icu | null>(null);
  useEffect(() => { intervalsFit().then(setIcu); }, []);
  const fit = useMemo(() => fitness(s, today, icu), [s.rides, s.plan, s.ramp, today, icu]);
  // Measured fitness since July with the summer peak as a line: where you are against your best, no projection.
  const spark = useMemo(() => {
    const pts = fit.days.filter((d) => !d.proj && d.d >= "2026-07-01");
    if (pts.length < 2) return { past: "", peakY: null as number | null };
    const max = Math.max(1, ...pts.map((d) => d.ctl)), n = pts.length - 1;
    const y = (v: number) => 46 - (v / max) * 42;
    return { past: pts.map((d, i) => `${i ? "L" : "M"}${((i / n) * 200).toFixed(1)} ${y(d.ctl).toFixed(1)}`).join(""), peakY: fit.peak ? y(fit.peak.ctl) : null };
  }, [fit, today]);
  const lightStrip = sun ? (() => { const a = (sun.rise - 300) / 1020 * 100, b = (sun.set - 300) / 1020 * 100; return `background: linear-gradient(90deg, var(--light-night) ${a - 4}%, var(--light-dawn) ${a}%, var(--light-day) ${a + 6}%, var(--light-day) ${b - 6}%, var(--dusk) ${b}%, var(--light-night) ${b + 4}%)`; })() : "";
  const [ideaTrack, setIdeaTrack] = useState<[number, number][] | null>(null);
  useEffect(() => { const id = ideas?.ideas[0]?.id; if (!id) return; routeDetail(id).then((x) => setIdeaTrack(x?.track ?? null)).catch(() => {}); }, [ideas?.ideas[0]?.id]);
  const statusWord = (d: PlanDay) => d.status === "booked" ? "Booked" : d.status === "ticked" ? "Chosen" : "Proposed";

  // The ready list, grouped as you'd lay it out: what to wear, the bike, the fuel.
  const BIKE = new Set(["Front light", "Rear light", "Roam charged"]);
  const groups = next ? [
    { name: "Wear", items: items.filter((it) => !BIKE.has(it) && !fuel!.items.includes(it)) },
    { name: "Bike", items: items.filter((it) => BIKE.has(it)) },
    { name: "Fuel", items: items.filter((it) => fuel!.items.includes(it)) },
  ].filter((g) => g.items.length) : [];
  const rainLine = span && next ? (span.rainFrom ? (toMin(span.rainFrom) <= toMin(next.start!) ? "Rain from the start." : `Rain from ${span.rainFrom}, ${toMin(span.rainFrom) - toMin(next.start!)} minutes in.`) : span.rainMax >= 30 ? `Rain up to ${span.rainMax}%.` : "Dry.") : "";
  const ready = next ? (
    <div class="ready-sheet">
      <div class="row-between">
        <span class="label">{when(next)} · {next.start}{purpose ? ` · ${purpose.title}` : ""}</span>
        <span class={`label${laid.size >= items.length ? " amber" : ""}`}>{Math.min(laid.size, items.length)} of {items.length}</span>
      </div>
      <span class="sheet-title">Ready for {when(next) === "Today" ? "today" : dayLong(next.date)}</span>
      <span class="soft">{fc ? `Feels ${fc.feels}° at ${next.start}. ` : ""}{rainLine}{span ? ` Wind up to ${span.windMax} km/h.` : ""}{dark ? ` ${dark} minutes in the dark.` : " All in daylight."}</span>
      {groups.map((g) => (
        <div class="ready-group">
          <div class="row-between"><span class="label">{g.name}</span>{g.name === "Fuel" && fuel?.line ? <span class="label quiet">{fuel.line.replace(/^Eat from 30 minutes in, then every 25\. /, "")}</span> : null}</div>
          {g.items.map((it) => (
            <button type="button" role="checkbox" aria-checked={laid.has(it)} class={`ready-row${laid.has(it) ? " on" : ""}`} onClick={() => toggleReady(next.date, it)}>
              <span class="box" aria-hidden="true">{laid.has(it) && <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 6.2l2.2 2.2L9.5 3.6" /></svg>}</span>
              <span>{it}</span>
            </button>
          ))}
        </div>
      ))}
      {fuel?.line && <span class="soft">Eat from 30 minutes in, then every 25.</span>}
      {next.date === today && next.start! >= "12:00" && <span class="soft">Carbs at lunch: {next.lunch ?? "pasta or rice"}.</span>}
      <button type="button" class="chat-act" onClick={() => { items.filter((it) => !laid.has(it)).forEach((it) => toggleReady(next.date, it)); setReadyOpen(false); }}><span>All laid out</span><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7" /></svg></button>
    </div>
  ) : null;

  return (
    <main class="screen signal today">
      <Head right={`W${String(weekNo).padStart(2, "0")} · ${dateLabel(today)}`} />
      <Pull onRefresh={pullRefresh} />

      <a class="coach-line stack" href="/coach">
        <span class="label">Coach</span>
        <span class="say">{coachLine(s, today)}</span>
      </a>

      <section class="stack">
        {askLegs && (
          <div class="card legs">
            <span class="label ink">Legs this morning?</span>
            <div class="segmented three" role="group" aria-label="Legs this morning">
              {(["fresh", "normal", "heavy"] as Legs[]).map((v) => <button type="button" onClick={() => legs(v)}>{v[0].toUpperCase() + v.slice(1)}</button>)}
            </div>
          </div>
        )}
        {next ? (
          <div class={`card ride-card brief${rideTrack ? " has-map" : ""}`}>
            {rideTrack && <div class="brief-map"><RouteMap points={rideTrack} h={200} tiles dark label={`Map of ${nextRoute?.n ?? "the route"}`} /></div>}
            <div class="brief-body">
              <div class="row-between brief-top">
                <span class="label amber">{when(next)}{next.date !== today ? ` ${dayNum(next.date)}` : ""} · {missedNow && next.date === today ? "Missed" : statusWord(next)}</span>
                {outIn && !missedNow && <span class="label">{outIn}</span>}
              </div>
              <span class="brief-name">{nextRoute?.n ?? "City loop, lit"}</span>
              <span class="brief-meta">{next.start} to {next.end}{nextRoute?.km ? ` · ${nextRoute.km} km` : ""}{nextRoute?.gain ? ` · ${nextRoute.gain} m` : ""}</span>
              {missedNow && next.date === today ? (
                <>
                  <span class="purpose-target">Nothing on Strava for {next.start} to {next.end}.</span>
                  <div class="brief-actions">
                    <a class="brief-go" href={`/week?d=${today}`}><span>Move it to another day</span><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg></a>
                  </div>
                </>
              ) : <>
              {purpose && (
                <div class="purpose">
                  <span class="label">The job · {purpose.title}</span>
                  <span class="purpose-target">{purpose.target}</span>
                  {!rideTrack && <span class="soft">{purpose.why}</span>}
                </div>
              )}
              <div class="brief-grid" aria-label="Conditions">
                <div><span class="label">Feels</span><span class="brief-n">{fc ? `${fc.feels}°` : "–"}</span></div>
                <div><span class="label">Wind</span><span class="brief-n">{rideWind ? rideWind.kmh : span ? span.windMax : "–"}{rideWind ? <small> {compass(rideWind.from)}</small> : null}</span></div>
                <div><span class="label">Rain</span><span class="brief-n">{span ? (span.rainFrom ? span.rainFrom : span.rainMax >= 30 ? `${span.rainMax}%` : "Dry") : "–"}</span></div>
                <div><span class="label">Light</span><span class="brief-n">{dark ? dark : "Day"}{dark ? <small> min dark</small> : null}</span></div>
              </div>
              <div class="brief-actions">
                {proposed
                  ? <button type="button" class="brief-go" onClick={async () => show(await bookRides([next.date]))}><span>Book it</span><span class="brief-go-r"><span>{next.start} · {when(next)}</span><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg></span></button>
                  : <button type="button" class="brief-go" onClick={() => setReadyOpen(true)}><span>Get ready</span><span class="brief-go-r"><span>{Math.min(laid.size, items.length)} of {items.length}</span><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg></span></button>}
                <button type="button" class="brief-edit" aria-label="Change the time or the route" onClick={() => setEditOpen(true)}><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M4 7h10M18 7h2M4 17h4M12 17h8" /><circle cx="16" cy="7" r="2" /><circle cx="10" cy="17" r="2" /></svg></button>
              </div>
              </>}
              <div class="brief-links">
                {proposed && <button type="button" class="text-btn" onClick={() => setReadyOpen(true)}>Get ready · {Math.min(laid.size, items.length)} of {items.length}</button>}
                {nextRoute?.komoot && <a class="text-btn" href={nextRoute.komoot} target="_blank" rel="noopener">Open in Komoot</a>}
                {next.date === today && dark > 0 && !missedNow && <button type="button" class="text-btn" onClick={share}>Share where I'm riding</button>}
                {(ideas?.ideas.length || litPicks.length) ? <button type="button" class="text-btn" onClick={() => setRoutesOpen(true)}>Other routes{ideas && ideas.date !== next.date ? ` · ${dayLong(ideas.date)}` : ""}</button> : null}
              </div>
            </div>
          </div>
        ) : (
          <div class="card">
            <span class="label">Nothing booked</span>
            <span class="say">The week is clear from here.</span>
            <a class="text-btn" href="/week">Open the week</a>
          </div>
        )}


        <div class="tiles">
          <a class="card tile" href="/week" aria-label={`This week: ${tileDone} of ${tileOf} ${byRides ? "rides" : "km"}`}>
            <span class="label">This week</span>
            <div class="tile-row">
              <Ring done={tileDone} of={Math.max(1, tileOf)} size={48} />
              <span class="tile-big">{tileDone}<small> of {tileOf}{byRides ? "" : " km"}</small></span>
            </div>
            <span class="tile-cap">{byRides ? `Rides, not km · ${weekPlanned} planned` : `${weekDone} ${weekDone === 1 ? "ride" : "rides"} so far`}</span>
          </a>
          <a class="card tile" href="/paris">
            <span class="label">Paris · {parisWeeks} wks</span>
            <span class="tile-big">{parisPct}<small>%</small></span>
            <span class="tile-bar" aria-hidden="true"><span style={`width:${parisPct}%`} /></span>
            <span class="tile-cap">{top?.km ?? 0} of Day 1’s {day1Km} km</span>
          </a>
          <div class="card tile">
            <span class="label">{next ? `At ${next.start}` : "Weather"}</span>
            {fc ? <span class="tile-big">{fc.feels}<small>° feels</small></span> : <span class="tile-big muted">–</span>}
            <span class="tile-cap">{fc ? `Wind ${fc.wind} km/h${fc.rain >= 0.2 ? ` · rain ${fc.rain} mm` : " · dry"}` : "Forecast from 16 days out"}</span>
          </div>
          <div class="card tile">
            <span class="label">Light{next && next.date !== today ? ` · ${dayShort(next.date)}` : ""}</span>
            <span class="tile-big">{Math.round(dark || 0)}<small> min dark</small></span>
            <span class="tile-light" aria-hidden="true" style={lightStrip} />
            <span class="tile-cap">Up {sun ? hhmm(sun.rise) : "–"} · down {sun ? hhmm(sun.set) : "–"}</span>
          </div>
          {fit.now && (
            <a class="card tile wide" href="/coach">
              <div class="row-between"><span class="label">Fitness</span><span class="label">{fit.from === "intervals" ? "intervals.icu, measured" : "Relative Effort, estimated"}</span></div>
              <div class="tile-row">
                <span class="tile-big">{Math.round(fit.now.ctl)}<small> now</small></span>
                <svg class="spark" viewBox="0 0 200 48" preserveAspectRatio="none" aria-hidden="true">
                  <path d={spark.past} class="spark-past" />
                  {spark.peakY != null && <line x1="0" x2="200" y1={spark.peakY} y2={spark.peakY} class="spark-peak" />}
                </svg>
                <span class="tile-big right">{fit.peak ? Math.round(fit.peak.ctl) : "–"}<small> peak</small></span>
              </div>
              <span class="tile-bar" aria-hidden="true"><span style={`width:${fit.peak && fit.peak.ctl > 0 ? Math.min(100, Math.round((fit.now.ctl / fit.peak.ctl) * 100)) : 0}%`} /></span>
              <span class="tile-cap">{fit.peak && fit.peak.ctl > fit.now.ctl + 3 ? `${Math.round((fit.now.ctl / fit.peak.ctl) * 100)}% of your summer peak (${dayLong(fit.peak.d)} ${dayNum(fit.peak.d)} ${new Date(fit.peak.d + "T12:00:00Z").toLocaleDateString("en-GB", { month: "short" })}). Back to it is the first target.` : "At your best so far. Every ride from here is new ground."}</span>
            </a>
          )}
          {lastRide && (
            <a class="card tile wide photo-tile" href={`/rides/${lastRide.id ?? 0}`}>
              <img src={lastRide.photo ?? photo("coach")} alt="" loading="lazy" decoding="async" />
              <div class="photo-cap">
                <span class="label" style="color:#fff">Last ride · {dateLabel(lastRide.d)}</span>
                <span class="tile-big" style="color:#fff">{lastRide.km}<small style="color:rgba(255,255,255,.75)"> km · {lastRide.m} m</small></span>
              </div>
            </a>
          )}
          {ideas && ideas.ideas[0] && (
            <button type="button" class="card tile wide idea-tile" onClick={() => setRoutesOpen(true)}>
              <div class="idea-tile-text">
                <span class="label">{dayLong(ideas.date)} · {ideas.ideas[0].planned ? "planned" : "idea"}</span>
                <span class="note-title">{ideas.ideas[0].name}</span>
                <span class="tile-cap">{ideas.ideas[0].km} km · {ideas.ideas[0].gain.toLocaleString("en-GB")} m · {hhmm(ideas.ideas[0].start)} to {hhmm(ideas.ideas[0].end)}</span>
                <span class="tile-link">Other routes</span>
              </div>
              {ideaTrack ? <RouteMap points={ideaTrack} label="" thumb /> : <span class="tile-km">{ideas.ideas[0].km}<small> km</small></span>}
            </button>
          )}
        </div>
      </section>

      {next && (
        <Sheet open={editOpen} onClose={() => setEditOpen(false)} label="Change the time or the route">
          <div class="edit-sheet">
            <span class="sheet-title">Change the time or the route</span>
            <RideEditor day={next} toast={show} kept={keptFor(next)} label={`${dayLong(next.date)} · ${statusWord(next)}`} compact />
          </div>
        </Sheet>
      )}
      {next && <Sheet open={readyOpen} onClose={() => setReadyOpen(false)} label="Get ready">{ready}</Sheet>}
      <Sheet open={routesOpen} onClose={() => setRoutesOpen(false)} label="Other routes">
        <div class="stack routes-sheet">
        {ideas && ideas.ideas.length > 0 && (
          <div class="ideas">
            <div class="row-between">
              <span class="label ink">Ideas for {dayLong(ideas.date)}</span>
              <span class="label">{ideas.target ? `Plan: ${Math.round(ideas.target)} km` : ideas.day ? "Long ride" : "Nothing planned yet"}</span>
            </div>
            {ideas.ideas.map((i) => (
              <div class={`idea${i.planned ? " on" : ""}`}>
                <div class="idea-text">
                  <span class="note-title">{i.name}</span>
                  <span class="label">{i.km} km · {i.gain.toLocaleString("en-GB")} m · {hhmm(i.start)} to {hhmm(i.end)}</span>
                  <span class="soft">{i.reasons.join(". ")}.</span>
                </div>
                {i.planned ? <span class="label amber">Planned</span> : <button type="button" class="pill small quiet-pill" onClick={() => pickIdea(i.id)} aria-label={`Ride ${i.name} on ${dayLong(ideas.date)}`}>Ride this</button>}
              </div>
            ))}
            {fresh && (
              <div class="idea fresh">
                <div class="idea-text">
                  <span class="label amber">New ground · Komoot</span>
                  <span class="note-title">{fresh.e.name}</span>
                  <span class="label">{fresh.total} km · {fresh.e.gain.toLocaleString("en-GB")} m · {hhmm(fresh.start)} to {hhmm(fresh.end)}</span>
                  <span class="soft">{fresh.reasons.join(". ")}. Lighting not surveyed: fine at the weekend, daylight only on a weekday.</span>
                  <a class="text-btn" href={fresh.e.url} target="_blank" rel="noopener">Open in Komoot</a>
                </div>
                <button type="button" class="pill small quiet-pill" onClick={rideNew} aria-label={`Ride ${fresh.e.name} on ${dayLong(ideas.date)}`}>Ride this</button>
              </div>
            )}
            <a class="text-btn" href="/routes/new">More new ground</a>
            <span class="label">{Object.values(outs).some((v) => v != null) ? "Wind from the forecast, on routes with a map. Times from your pace model, an estimate." : "Times from your pace model, an estimate. Import a route's GPX to get the wind on it."}</span>
          </div>
        )}

        {eve && litPicks.length > 0 && (
          <div class="ideas">
            <div class="row-between"><span class="label ink">Lit loops for {dayLong(eve.date)} evening</span><span class="label">Done by {s.settings.latestFinish ?? "21:30"}</span></div>
            {litPicks.map((g) => (
              <div class="idea">
                <div class="idea-text">
                  <span class="note-title">{g.e.name}</span>
                  <span class="label">{g.total} km · {g.e.gain.toLocaleString("en-GB")} m · {hhmm(g.start)} to {hhmm(g.end)}</span>
                  <span class="soft">Lit {g.e.light!.lit}%, 30 mph streets {g.e.light!.likely}%, not known {g.e.light!.unknown}%{g.approach ? `. Starts ${g.e.startKm} km away` : ""}.</span>
                </div>
                <button type="button" class="pill small quiet-pill" onClick={() => rideLit(g)} aria-label={`Ride ${g.e.name} on ${dayLong(eve.date)}`}>Ride this</button>
              </div>
            ))}
            <span class="label">Lighting from OpenStreetMap. Your own check on the ride overrides it.</span>
          </div>
        )}

        </div>
      </Sheet>

      {/* The one action the card can't hold: how it felt, a missed ride, an empty week. */}
      <div class="dock-wrap">
        {toast}
        {moment.kind === "debrief" && (
          <div class="dock">
            <span class="label ink">How did {moment.date === today ? "today" : `${dayLong(moment.date)}'s ride`} feel?</span>
            <div class="feel">
              {FEELS.map((f) => <button type="button" class="chip" onClick={() => { setFeel(moment.date, f.id); show(`Logged: ${f.label.toLowerCase()}`, () => setFeel(moment.date, null)); }}>{f.label}</button>)}
            </div>
          </div>
        )}
        {moment.kind === "none" && (
          <a class="dock-block quiet" href="/week?w=next"><span>Plan next week</span><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg></a>
        )}
      </div>
    </main>
  );
}

/** A ride day carries no kept window of its own: the walls (work, the 21:30 finish) do the keeping. */
function keptFor(_d: PlanDay): Kept[] { return []; }

export function fmtDur(min: number) {
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return `${h} h ${String(m).padStart(2, "0")}`;
}
export function rampWeekNo(ramp: { w: string }[], monday: string) {
  const i = ramp.findIndex((r) => r.w === monday);
  return i >= 0 ? i + 1 : "–";
}
